import { useNavigate } from 'react-router-dom';
import { UnitType, unitTypeToUnits, type Item } from '../../models/Item';
import { FormFieldValue } from '../forms/FormFieldValue';
import { ChangeEvent, SubmitEvent, useCallback, useState } from 'react';
import { Box, Button, FormControlLabel, Grid, MenuItem, Stack, Switch, TextField } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import NumberField from '../NumberField';

export interface ItemFormState {
  values: Partial<Omit<Item, 'id'>>;
  errors: Partial<Record<keyof ItemFormState['values'], string>>;
}

export interface ItemFormProps {
  formState: ItemFormState;
  onFieldChange: (
    name: keyof ItemFormState['values'],
    value: FormFieldValue,
  ) => void;
  onSubmit: (formValues: Partial<ItemFormState['values']>) => Promise<void>;
  onReset?: (formValues: Partial<ItemFormState['values']>) => void;
  submitButtonLabel: string;
  backButtonPath?: string;
}

export default function ItemForm(props: ItemFormProps) {
  const {
    formState,
    onFieldChange,
    onSubmit,
    onReset,
    submitButtonLabel,
    backButtonPath,
  } = props;

  const formValues = formState.values;
  const formErrors = formState.errors;

  const navigate = useNavigate();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = useCallback(
    async (event: SubmitEvent) => {
      event.preventDefault();

      setIsSubmitting(true);
      try {
        await onSubmit(formValues);
      } finally {
        setIsSubmitting(false);
      }
    },
    [formValues, onSubmit],
  );

  const handleTextFieldChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onFieldChange(
        event.target.name as keyof ItemFormState['values'],
        event.target.value,
      );
    },
    [onFieldChange],
  );

  const handleNumberFieldChange = useCallback(
    (value: number | null, fieldName: string) => {
      onFieldChange(
        fieldName as keyof ItemFormState['values'],
        value || 0,
      );
    },
    [onFieldChange],
  );

  const handleCheckboxFieldChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onFieldChange(
        event.target.name as keyof ItemFormState['values'],
        event.target.checked
      );
    },
    [onFieldChange],
  );

  const handleReset = useCallback(() => {
    if (onReset) {
      onReset(formValues);
    }
  }, [formValues, onReset]);

  const handleBack = useCallback(() => {
    navigate(backButtonPath ?? '/dashboard/items');
  }, [navigate, backButtonPath]);

  const getUnits = useCallback((unitType: UnitType | undefined) => {
    return unitTypeToUnits(unitType as UnitType);
  }, []);

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      noValidate
      autoComplete="off"
      onReset={handleReset}
      sx={{ width: '100%' }}
    >
      <div>
        <Grid container spacing={2} sx={{ mb: 2, width: '100%' }}>
          <Grid size={{ xs: 6, sm: 2 }} sx={{ display: 'flex' }}>
            <TextField
              value={formValues.name ?? ''}
              onChange={handleTextFieldChange}
              name="name"
              label="Name"
              variant="outlined"
              error={!!formErrors.name}
              helperText={formErrors.name ?? ' '}
              slotProps={{ inputLabel: { shrink: true } }}
              required
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }} sx={{ display: 'flex' }}>
            <TextField
              value={formValues.description ?? ''}
              onChange={handleTextFieldChange}
              name="description"
              label="Description"
              slotProps={{ inputLabel: { shrink: true } }}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }} sx={{ display: 'flex' }}>
            <TextField
              select
              value={String(formValues?.unitType ?? '0')}
              onChange={handleTextFieldChange}
              label="Unit"
              name="unitType"
              error={!!formErrors.unitType}
              fullWidth
              required
            >
              <MenuItem value={UnitType.POUND}>Pound</MenuItem>
              <MenuItem value={UnitType.OUNCE}>Ounces</MenuItem>
              <MenuItem value={UnitType.ITEM}>Item</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }} sx={{ paddingTop: '.5em' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formValues.isIngredient}
                  name="isIngredient"
                  onChange={handleCheckboxFieldChange}
                  slotProps={{
                    input: { 'aria-label': 'controlled' },
                  }} />}
              label="Ingredient?" />
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={{ mb: 2, width: '100%' }}>
          <Grid size={{ xs: 6, sm: 2 }} sx={{ display: 'flex' }}>
            <NumberField
              value={formValues?.standardShippingQuantity || null}
              onValueChange={(event) => handleNumberFieldChange(event, 'standardShippingQuantity')}
              helperText={' '}
              name="standardShippingQuantity"
              label="Standard Shipping Qty"
              weightFieldUnits={getUnits(formValues?.unitType as UnitType)}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }} sx={{ display: 'flex' }}>
            <NumberField
              value={formValues?.minOrderQuantity || null}
              onValueChange={(event) => handleNumberFieldChange(event, 'minOrderQuantity')}
              helperText={' '}
              name="minOrderQuantity"
              label="Min Order Qty"
              weightFieldUnits={getUnits(formValues?.unitType as UnitType)}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }} sx={{ display: 'flex' }}>
            <NumberField
              value={formValues?.maxOrderQuantity || null}
              onValueChange={(event) => handleNumberFieldChange(event, 'maxOrderQuantity')}
              helperText={' '}
              name="maxOrderQuantity"
              label="Max Order Qty"
              weightFieldUnits={getUnits(formValues?.unitType as UnitType)}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={{ mb: 2, width: '100%' }}>
          <Grid size={{ xs: 9, sm: 4 }} sx={{ display: 'flex' }}>
            <TextField
              value={formValues?.orderNotes || ''}
              onChange={handleTextFieldChange}
              name="orderNotes"
              label="Order Notes"
              helperText={' '}
              slotProps={{ inputLabel: { shrink: true } }}
              fullWidth
            />
          </Grid>
        </Grid>
      </div>
      <Stack direction="row" spacing={2} justifyContent="space-between">
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
        >
          Back
        </Button>
        <Button
          type="submit"
          variant="contained"
          size="large"
          color="success"
          startIcon={<SaveIcon />}
          loading={isSubmitting}
        >
          {submitButtonLabel}
        </Button>
      </Stack>
    </Box>
  );
}
